import axios from 'axios';

import { ICD_API, ICD_API_LANGUAGE } from 'src/config-global';

// ----------------------------------------------------------------------
// Cliente HTTP del despliegue propio de la API CIE-11 de la OMS.
//
// NO usa src/utils/axios.js por dos motivos independientes: aquella instancia
// cuelga el JWT de Medicore en axios.defaults.headers -el CORS de grumeba no
// admite Authorization, así que el preflight falla, y además mandarlo filtraría
// el token de sesión a otro origen-, y su interceptor rechaza con el cuerpo en
// vez del AxiosError, con lo que un 404 y un 500 se vuelven indistinguibles.
//
// Las tres cabeceras son OBLIGATORIAS: sin Accept-Language el servicio responde
// 404, y con un idioma no cargado responde 500 con cuerpo vacío.
// ----------------------------------------------------------------------

const TIMEOUT_MS = 12000;

const icdAxios = axios.create({
  baseURL: ICD_API,
  timeout: TIMEOUT_MS,
  headers: {
    Accept: 'application/json',
    'API-Version': 'v2',
    'Accept-Language': ICD_API_LANGUAGE || 'es',
  },
});

// No basta con no usar src/utils/axios.js: `setSession` escribe el JWT en
// `axios.defaults.headers.common` del axios GLOBAL, y `axios.create()` copia
// esos defaults en el instante del import. Al recargar una pantalla ya
// autenticada este módulo se evalúa DESPUÉS del login, así que la instancia
// nacería con Authorization: preflight rechazado y token filtrado a grumeba.
icdAxios.interceptors.request.use((config) => {
  if (typeof config.headers?.delete === 'function') config.headers.delete('Authorization');
  else if (config.headers) delete config.headers.Authorization;
  return config;
});

export const ICD_ERROR = Object.freeze({
  CANCELADO: 'cancelado',
  TIMEOUT: 'timeout',
  RED: 'red',
  SERVIDOR: 'servidor',
  NO_ENCONTRADO: 'no-encontrado',
});

// Mensajes en español, aptos para mostrar tal cual al usuario: la UI nunca debe
// dejar un desplegable vacío ante un fallo, porque se lee como "no existe".
const MENSAJES = {
  [ICD_ERROR.CANCELADO]: 'Búsqueda de diagnósticos cancelada.',
  [ICD_ERROR.TIMEOUT]: 'El servicio de diagnósticos tardó demasiado en responder.',
  [ICD_ERROR.RED]: 'No se pudo contactar el servicio de diagnósticos.',
  [ICD_ERROR.SERVIDOR]: 'El servicio de diagnósticos respondió con un error.',
  [ICD_ERROR.NO_ENCONTRADO]: 'El servicio de diagnósticos no encontró ese recurso.',
};

/**
 * Error del módulo CIE-11. `message` ya viene en español; `tipo` es uno de
 * ICD_ERROR y `estado` el código HTTP cuando lo hubo.
 */
export class IcdError extends Error {
  constructor(tipo, estado = null, causa) {
    super(MENSAJES[tipo] ?? MENSAJES[ICD_ERROR.SERVIDOR]);
    this.name = 'IcdError';
    this.tipo = tipo;
    this.estado = estado;
    if (causa !== undefined) this.cause = causa;
  }
}

const esCancelacion = (error, signal) =>
  axios.isCancel(error) ||
  signal?.aborted === true ||
  error?.name === 'CanceledError' ||
  error?.name === 'AbortError' ||
  error?.code === 'ERR_CANCELED';

/**
 * Traduce cualquier fallo de transporte a IcdError. El orden importa: una
 * petición abortada por el llamador no es un error que deba pintarse.
 * @param {*} error
 * @param {AbortSignal} [signal]
 * @returns {IcdError}
 */
const traducirError = (error, signal) => {
  if (error instanceof IcdError) return error;
  if (esCancelacion(error, signal)) return new IcdError(ICD_ERROR.CANCELADO, null, error);
  if (error?.code === 'ECONNABORTED') return new IcdError(ICD_ERROR.TIMEOUT, null, error);

  const estado = error?.response?.status ?? null;
  if (estado === null) return new IcdError(ICD_ERROR.RED, null, error);
  if (estado === 404) return new IcdError(ICD_ERROR.NO_ENCONTRADO, estado, error);
  return new IcdError(ICD_ERROR.SERVIDOR, estado, error);
};

/**
 * GET contra la API CIE-11. Devuelve directamente `response.data`.
 * @param {string} ruta ruta relativa a ICD_API, con barra inicial
 * @param {{params?: object, signal?: AbortSignal}} [opciones]
 * @returns {Promise<*>} datos de la respuesta
 * @throws {IcdError}
 */
export async function icdGet(ruta, { params, signal } = {}) {
  try {
    const respuesta = await icdAxios.get(ruta, { params, signal });
    return respuesta.data;
  } catch (error) {
    throw traducirError(error, signal);
  }
}
